import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/lib/store'
import { Button } from '@/components/ui/button'
import { FileUpload } from '@/components/ui/file-upload'
import { Toast } from '@/components/ui/toast'
import {
  AlertCircle,
  Store,
  CreditCard,
  Upload,
  Globe,
  Rocket,
  Shield,
  ChevronDown,
  Star,
} from 'lucide-react'
import { supabase } from '@/lib/supabase'

interface SellerForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  storeName: string;
  storeDescription: string;
  storeCategories: string[];
  address: string;
  subscription: 'free' | 'premium';
  enableNegotiation: boolean;
  idFrontDocument: File | null;
  idBackDocument: File | null;
  proofOfAddress: File | null;
  selfieWithId: File | null;
}

const CATEGORIES = [
  'Electronics',
  'Fashion',
  'Home & Garden', 
  'Sports',
  'Beauty',
  'Books',
  'Toys',
  'Other',
]

export function SellerRegisterPage() {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const [existingApplication, setExistingApplication] = useState(false)
  const [loading, setLoading] = useState(true)
  const [currentSection, setCurrentSection] = useState(0)
  const [showToast, setShowToast] = useState(false)
  const [form, setForm] = useState<SellerForm>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    storeName: '',
    storeDescription: '',
    storeCategories: [],
    address: '',
    subscription: 'free',
    enableNegotiation: false,
    idFrontDocument: null,
    idBackDocument: null,
    proofOfAddress: null,
    selfieWithId: null,
  })
  const [error, setError] = useState('')
  const [showFaq, setShowFaq] = useState<number | null>(null)
  
  // Add file input refs for mobile
  const idFrontRef = useRef<HTMLInputElement>(null)
  const idBackRef = useRef<HTMLInputElement>(null)
  const proofOfAddressRef = useRef<HTMLInputElement>(null)
  const selfieWithIdRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    checkExistingApplication()
  }, [])

  const checkExistingApplication = async () => {
    if (!user) {
      navigate('/login')
      return
    }

    try {
      const { data, error } = await supabase
        .from('seller_stores')
        .select('id, status')
        .eq('seller_id', user.id)
        .maybeSingle()

      if (error && error.code !== 'PGRST116') {
        throw error
      }

      if (data) {
        setExistingApplication(true)
        navigate('/seller/application-status')
      }
    } catch (err) {
      console.error('Error checking application:', err)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (existingApplication) {
    return null
  }

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: keyof Pick<SellerForm, 'idFrontDocument' | 'idBackDocument' | 'proofOfAddress' | 'selfieWithId'>
  ) => {
    console.log(`handleFileChange called for ${field}`, e.target.files);
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      
      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError(`Le fichier est trop volumineux. La taille maximale est de 5MB.`);
        return;
      }
      
      setForm((prev) => ({ ...prev, [field]: e.target.files![0] }));
      console.log(`File set for ${field}:`, file.name, file.size);
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) {
      setError('You must be logged in to submit an application')
      return
    }

    setError('')
    setLoading(true)

    try {
      const { data: existingData } = await supabase
        .from('seller_stores')
        .select('id')
        .eq('seller_id', user.id)
        .maybeSingle()

      if (existingData) {
        setError('You have already submitted a seller application')
        return
      }

      const documentUrls: Record<string, string | null> = {
        id_front: null,
        id_back: null,
        proof_of_address: null,
        selfie_with_id: null
      }

      const uploadDocument = async (file: File | null, key: string) => {
        if (!file || !user) return null

        const fileExt = file.name.split('.').pop()
        const fileName = `${user.id}/${key}.${fileExt}`

        console.log(`Uploading ${key} document:`, fileName);
        
        try {
          const { error: uploadError } = await supabase.storage
            .from('seller-documents')
            .upload(fileName, file, { upsert: true })

          if (uploadError) {
            console.error(`Error uploading ${key}:`, uploadError);
            throw uploadError;
          }

          const { data: { publicUrl } } = supabase.storage
            .from('seller-documents')
            .getPublicUrl(fileName)

          console.log(`${key} uploaded successfully, URL:`, publicUrl);
          return publicUrl;
        } catch (err) {
          console.error(`Error in uploadDocument for ${key}:`, err);
          throw err;
        }
      }

      await Promise.all([
        (async () => {
          documentUrls.id_front = await uploadDocument(form.idFrontDocument, 'id-front')
        })(),
        (async () => {
          documentUrls.id_back = await uploadDocument(form.idBackDocument, 'id-back')
        })(),
        (async () => {
          documentUrls.proof_of_address = await uploadDocument(form.proofOfAddress, 'proof-of-address')
        })(),
        (async () => {
          documentUrls.selfie_with_id = await uploadDocument(form.selfieWithId, 'selfie-with-id')
        })()
      ])

      console.log('All documents uploaded, document URLs:', documentUrls);

      const { error: storeError } = await supabase
        .from('seller_stores')
        .insert([
          {
            seller_id: user.id,
            name: form.storeName,
            description: form.storeDescription,
            categories: form.storeCategories,
            address: form.address,
            subscription_type: form.subscription,
            enable_negotiation: form.enableNegotiation,
            verification_status: 'pending_review',
            status: 'pending',
            document_urls: documentUrls,
            applicant_name: `${form.firstName} ${form.lastName}`,
            applicant_email: form.email,
            applicant_phone: form.phone
          }
        ])

      if (storeError) {
        console.error('Error inserting seller store:', storeError);
        throw storeError;
      }

      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          role: 'seller',
          full_name: `${form.firstName} ${form.lastName}`,
          phone_number: form.phone,
          email: form.email
        })
        .eq('id', user.id)

      if (profileError) {
        console.error('Error updating profile:', profileError);
        throw profileError;
      }

      setShowToast(true)
      setTimeout(() => {
        navigate('/')
      }, 2000)
    } catch (err) {
      console.error('Error submitting application:', err);
      setError('Failed to submit application. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const sections = [
    {
      title: 'Personal Information',
      description: 'Tell us about yourself',
      content: (
        <div className="space-y-8">
          <div className="grid grid-cols-1 gap-y-8 gap-x-6 sm:grid-cols-2">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                First Name
              </label>
              <input
                type="text"
                id="firstName"
                required
                value={form.firstName}
                onChange={(e) => setForm((prev) => ({ ...prev, firstName: e.target.value }))}
                className="block w-full h-12 px-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                Last Name
              </label>
              <input
                type="text"
                id="lastName"
                required
                value={form.lastName}
                onChange={(e) => setForm((prev) => ({ ...prev, lastName: e.target.value }))}
                className="block w-full h-12 px-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                id="email"
                required
                value={form.email}
                onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                className="block w-full h-12 px-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                required
                value={form.phone}
                onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                className="block w-full h-12 px-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Store Information',
      description: 'Set up your store profile',
      content: (
        <div className="space-y-8">
          <div>
            <label htmlFor="storeName" className="block text-sm font-medium text-gray-700 mb-2">
              Store Name
            </label>
            <input
              type="text"
              id="storeName"
              required
              value={form.storeName}
              onChange={(e) => setForm((prev) => ({ ...prev, storeName: e.target.value }))}
              className="block w-full h-12 px-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          <div>
            <label htmlFor="storeDescription" className="block text-sm font-medium text-gray-700 mb-2">
              Store Description
            </label>
            <textarea
              id="storeDescription"
              rows={4}
              value={form.storeDescription}
              onChange={(e) => setForm((prev) => ({ ...prev, storeDescription: e.target.value }))}
              className="block w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Product Categories
            </label>
            <div className="mt-2 grid grid-cols-2 gap-3">
              {CATEGORIES.map((category) => (
                <label
                  key={category}
                  className="relative flex items-center px-4 py-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={form.storeCategories.includes(category)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setForm((prev) => ({
                          ...prev,
                          storeCategories: [...prev.storeCategories, category],
                        }))
                      } else {
                        setForm((prev) => ({
                          ...prev,
                          storeCategories: prev.storeCategories.filter((c) => c !== category),
                        }))
                      }
                    }}
                    className="h-5 w-5 text-primary border-2 border-gray-300 rounded focus:ring-primary"
                  />
                  <span className="ml-3 text-sm text-gray-900">{category}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
              Store Address (for pickup option)
            </label>
            <textarea
              id="address"
              rows={3}
              value={form.address}
              onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value }))}
              className="block w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
        </div>
      ),
    },
    {
      title: 'Subscription & Features',
      description: 'Choose your plan and features',
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div
              className={`relative rounded-lg border-2 p-6 cursor-pointer transition-colors ${
                form.subscription === 'free'
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => setForm((prev) => ({ ...prev, subscription: 'free' }))}
            >
              <div className="flex items-center">
                <Store className="h-8 w-8 text-primary" />
                <div className="ml-4">
                  <h4 className="text-lg font-medium text-gray-900">Free Plan</h4>
                  <p className="mt-1 text-sm text-gray-500">Perfect for starting out</p>
                </div>
              </div>
              <ul className="mt-4 space-y-2 text-sm text-gray-700">
                <li className="flex items-center">
                  <Star className="h-4 w-4 text-primary mr-2" />
                  Up to 3 products per month
                </li>
                <li className="flex items-center">
                  <Star className="h-4 w-4 text-primary mr-2" />
                  Basic analytics
                </li>
                <li className="flex items-center">
                  <Star className="h-4 w-4 text-primary mr-2" />
                  Standard support
                </li>
              </ul>
              <div className="mt-6">
                <p className="text-2xl font-bold text-gray-900">$0</p>
                <p className="text-sm text-gray-500">per month</p>
              </div>
            </div>

            <div
              className={`relative rounded-lg border-2 p-6 cursor-pointer transition-colors ${
                form.subscription === 'premium'
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => setForm((prev) => ({ ...prev, subscription: 'premium' }))}
            >
              <div className="flex items-center">
                <CreditCard className="h-8 w-8 text-primary" />
                <div className="ml-4">
                  <h4 className="text-lg font-medium text-gray-900">Premium Plan</h4>
                  <p className="mt-1 text-sm text-gray-500">For growing businesses</p>
                </div>
              </div>
              <ul className="mt-4 space-y-2 text-sm text-gray-700">
                <li className="flex items-center">
                  <Star className="h-4 w-4 text-primary mr-2" />
                  Up to 15 products per month
                </li>
                <li className="flex items-center">
                  <Star className="h-4 w-4 text-primary mr-2" />
                  Advanced analytics
                </li>
                <li className="flex items-center">
                  <Star className="h-4 w-4 text-primary mr-2" />
                  Priority support
                </li>
                <li className="flex items-center">
                  <Star className="h-4 w-4 text-primary mr-2" />
                  Featured products
                </li>
              </ul>
              <div className="mt-6">
                <p className="text-2xl font-bold text-gray-900">$15</p>
                <p className="text-sm text-gray-500">per month</p>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="enableNegotiation"
                checked={form.enableNegotiation}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, enableNegotiation: e.target.checked }))
                }
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
              />
              <label htmlFor="enableNegotiation" className="ml-2 text-sm text-gray-900">
                Enable price negotiation for products
              </label>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Allow buyers to negotiate prices. You can set minimum prices for individual products.
            </p>
          </div>
        </div>
      ),
    },
    {
      title: 'Verification',
      description: 'Verify your identity',
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="idFrontDocument" className="block text-sm font-medium text-gray-700 mb-2">
                ID Card (Front) *
              </label>
              <div className="relative border-2 border-dashed border-gray-300 rounded-lg hover:border-primary transition-colors">
                <input
                  ref={idFrontRef}
                  type="file"
                  id="idFrontDocument"
                  name="idFrontDocument"
                  accept="image/*"
                  className="sr-only"
                  onChange={(e) => handleFileChange(e, 'idFrontDocument')}
                  required
                />
                <button
                  type="button"
                  onClick={() => idFrontRef.current?.click()}
                  className="w-full h-full flex flex-col items-center justify-center p-6"
                >
                  {form.idFrontDocument ? (
                    <>
                      <div className="text-primary bg-primary/10 rounded-full p-2">
                        <Upload className="h-5 w-5" />
                      </div>
                      <p className="mt-2 text-sm text-primary font-medium truncate max-w-full">
                        {form.idFrontDocument.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(form.idFrontDocument.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 text-gray-400" />
                      <p className="mt-1 text-sm text-gray-500">
                        Cliquez pour ajouter
                      </p>
                    </>
                  )}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="idBackDocument" className="block text-sm font-medium text-gray-700 mb-2">
                ID Card (Back) *
              </label>
              <div className="relative border-2 border-dashed border-gray-300 rounded-lg hover:border-primary transition-colors">
                <input
                  ref={idBackRef}
                  type="file"
                  id="idBackDocument"
                  name="idBackDocument"
                  accept="image/*"
                  className="sr-only"
                  onChange={(e) => handleFileChange(e, 'idBackDocument')}
                  required
                />
                <button
                  type="button"
                  onClick={() => idBackRef.current?.click()}
                  className="w-full h-full flex flex-col items-center justify-center p-6"
                >
                  {form.idBackDocument ? (
                    <>
                      <div className="text-primary bg-primary/10 rounded-full p-2">
                        <Upload className="h-5 w-5" />
                      </div>
                      <p className="mt-2 text-sm text-primary font-medium truncate max-w-full">
                        {form.idBackDocument.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(form.idBackDocument.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 text-gray-400" />
                      <p className="mt-1 text-sm text-gray-500">
                        Cliquez pour ajouter
                      </p>
                    </>
                  )}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="proofOfAddress" className="block text-sm font-medium text-gray-700 mb-2">
                Proof of Address *
              </label>
              <div className="relative border-2 border-dashed border-gray-300 rounded-lg hover:border-primary transition-colors">
                <input
                  ref={proofOfAddressRef}
                  type="file"
                  id="proofOfAddress"
                  name="proofOfAddress"
                  accept="image/*,.pdf"
                  className="sr-only"
                  onChange={(e) => handleFileChange(e, 'proofOfAddress')}
                  required
                />
                <button
                  type="button"
                  onClick={() => proofOfAddressRef.current?.click()}
                  className="w-full h-full flex flex-col items-center justify-center p-6"
                >
                  {form.proofOfAddress ? (
                    <>
                      <div className="text-primary bg-primary/10 rounded-full p-2">
                        <Upload className="h-5 w-5" />
                      </div>
                      <p className="mt-2 text-sm text-primary font-medium truncate max-w-full">
                        {form.proofOfAddress.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(form.proofOfAddress.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 text-gray-400" />
                      <p className="mt-1 text-sm text-gray-500">
                        Cliquez pour ajouter
                      </p>
                    </>
                  )}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="selfieWithId" className="block text-sm font-medium text-gray-700 mb-2">
                Selfie with ID *
              </label>
              <div className="relative border-2 border-dashed border-gray-300 rounded-lg hover:border-primary transition-colors">
                <input
                  ref={selfieWithIdRef}
                  type="file"
                  id="selfieWithId"
                  name="selfieWithId"
                  accept="image/*"
                  className="sr-only"
                  onChange={(e) => handleFileChange(e, 'selfieWithId')}
                  required
                />
                <button
                  type="button"
                  onClick={() => selfieWithIdRef.current?.click()}
                  className="w-full h-full flex flex-col items-center justify-center p-6"
                >
                  {form.selfieWithId ? (
                    <>
                      <div className="text-primary bg-primary/10 rounded-full p-2">
                        <Upload className="h-5 w-5" />
                      </div>
                      <p className="mt-2 text-sm text-primary font-medium truncate max-w-full">
                        {form.selfieWithId.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(form.selfieWithId.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 text-gray-400" />
                      <p className="mt-1 text-sm text-gray-500">
                        Cliquez pour ajouter
                      </p>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-blue-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-800">
              Important Instructions:
            </h4>
            <ul className="mt-2 text-sm text-blue-700 list-disc list-inside space-y-1">
              <li>Upload clear, high-quality images of both sides of your ID card</li>
              <li>Ensure all text on the ID is clearly visible</li>
              <li>Your selfie should show you holding your ID next to your face</li>
              <li>Proof of address must be less than 3 months old</li>
            </ul>
          </div>
        </div>
      ),
    },
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {showToast && (
        <Toast
          type="success"
          title="Application Submitted Successfully!"
          message="You will be redirected to the home page."
          onClose={() => setShowToast(false)}
          position="center"
        />
      )}
      
      <div className="relative bg-primary text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-8 items-center">
            <div>
              <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl">
                Transformez votre passion en business avec AEM Marketplace !
              </h1>
              <p className="mt-4 text-lg">
                Vendez facilement vos produits et atteignez des milliers de clients.
                Rejoignez notre communauté et boostez votre activité en ligne avec des
                outils professionnels !
              </p>
            </div>
            <div className="mt-12 lg:mt-0">
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                  <Globe className="h-8 w-8" />
                  <h3 className="mt-4 text-lg font-medium">Visibilité Maximale</h3>
                  <p className="mt-2 text-sm text-gray-100">
                    Touchez des milliers de clients potentiels
                  </p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                  <Rocket className="h-8 w-8" />
                  <h3 className="mt-4 text-lg font-medium">Outils Marketing Puissants</h3>
                  <p className="mt-2 text-sm text-gray-100">
                    Boostez vos ventes avec des promotions et des vidéos
                  </p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                  <Shield className="h-8 w-8" />
                  <h3 className="mt-4 text-lg font-medium">Sécurité & Support</h3>
                  <p className="mt-2 text-sm text-gray-100">
                    Assistance 24/7 et transactions sécurisées
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-xl shadow-xl overflow-hidden">
          <div className="border-b border-gray-200">
            <div className="px-4 sm:px-6 lg:px-8">
              <nav className="-mb-px flex space-x-8">
                {sections.map((section, index) => (
                  <button
                    key={section.title}
                    onClick={() => setCurrentSection(index)}
                    className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                      currentSection === index
                        ? 'border-primary text-primary'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <span className="bg-gray-100 rounded-full w-6 h-6 inline-flex items-center justify-center mr-2">
                      {index + 1}
                    </span>
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="px-4 py-8 sm:px-6 lg:px-8">
              {error && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  {sections[currentSection].title}
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  {sections[currentSection].description}
                </p>
              </div>

              {sections[currentSection].content}

              <div className="mt-8 flex justify-between">
                {currentSection > 0 && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setCurrentSection(currentSection - 1)}
                  >
                    Previous
                  </Button>
                )}
                {currentSection < sections.length - 1 ? (
                  <Button
                    type="button"
                    variant="primary"
                    className="ml-auto"
                    onClick={() => setCurrentSection(currentSection + 1)}
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    variant="primary"
                    className="ml-auto"
                    disabled={loading}
                  >
                    {loading ? (
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      'Create Seller Account'
                    )}
                  </Button>
                )}
              </div>
            </div>
          </form>
        </div>

        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-900">Questions Fréquentes</h2>
          <dl className="mt-6 space-y-4">
            {[
              {
                question: "Combien coûte l'inscription ?",
                answer:
                  "L'inscription est gratuite. Vous pouvez choisir entre un plan gratuit (3 produits/mois) ou premium à $15/mois (15 produits/mois).",
              },
              {
                question: "Comment puis-je recevoir mes paiements ?",
                answer:
                  "Vous pouvez recevoir vos paiements via MonCash, PayPal ou en présentiel à la livraison.",
              
              },
              {
                question: "Puis-je modifier mes produits après publication ?",
                answer:
                  "Oui, vous pouvez modifier vos produits à tout moment via votre tableau de bord vendeur.",
              },
              {
                question: "Que se passe-t-il en cas de plainte ?",
                answer:
                  "Nous examinerons la plainte et travaillerons avec vous pour résoudre le problème. Des plaintes répétées peuvent entraîner la suspension du compte.",
              }
            ].map((faq, index) => (
              <div key={index} className="border-b border-gray-200 pb-4">
                <button
                  onClick={() => setShowFaq(showFaq === index ? null : index)}
                  className="w-full text-left"
                >
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900">{faq.question}</h3>
                    <ChevronDown
                      className={`h-5 w-5 text-gray-500 transition-transform ${
                        showFaq === index ? 'rotate-180' : ''
                      }`}
                    />
                  </div>
                </button>
                {showFaq === index && (
                  <p className="mt-4 text-gray-600">{faq.answer}</p>
                )}
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  )
}